import { FunctionComponent } from "react";
import { Box } from "@mui/material";
import { Datepicker, locale, type MbscDatepickerChangeEvent } from "@mobiscroll/react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { managementToolbarMobiscrollOutlineSx } from "../utils/management-toolbar.styles";
import { ManagementToolbarField } from "./ManagementToolbarField";

const PERIOD_LABEL_ID = "management-work-status-period-label";
const PERIOD_INPUT_ID = "management-work-status-period-input";

type ManagementWorkStatusDateRangeProps = {
  rangeStart: DateTime;
  rangeEnd: DateTime;
  onChange: (rangeStart: DateTime, rangeEnd: DateTime) => void;
};

function parseRangePickerValue(
  value: MbscDatepickerChangeEvent["value"]
): { start: DateTime; end: DateTime } | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const [from, to] = value as (Date | null | undefined)[];
  if (!(from instanceof Date) || !(to instanceof Date)) {
    return null;
  }

  return {
    start: DateTime.fromJSDate(from).startOf("day"),
    end: DateTime.fromJSDate(to).startOf("day")
  };
}

/**
 * Inclusive period range picker for work-status detail.
 */
export const ManagementWorkStatusDateRange: FunctionComponent<
  ManagementWorkStatusDateRangeProps
> = ({ rangeStart, rangeEnd, onChange }) => {
  const { t, i18n } = useTranslation();
  const { dateFormat } = useTimeFormat();

  return (
    <ManagementToolbarField
      label={t("_management_period")}
      labelId={PERIOD_LABEL_ID}
      htmlFor={PERIOD_INPUT_ID}
    >
      <Box
        data-testid="management-work-status-date-range"
        role="group"
        aria-labelledby={PERIOD_LABEL_ID}
        sx={theme => managementToolbarMobiscrollOutlineSx(theme)}
      >
        <Datepicker
          select="range"
          rangeHighlight
          inputStyle="outline"
          data-testid="management-work-status-period-picker"
          value={[rangeStart.toJSDate(), rangeEnd.toJSDate()]}
          onChange={(event: MbscDatepickerChangeEvent) => {
            const nextRange = parseRangePickerValue(event.value);
            if (!nextRange) {
              return;
            }
            onChange(nextRange.start, nextRange.end);
          }}
          dateFormat={dateFormat.toUpperCase()}
          locale={locale[i18n.language]}
          controls={["calendar"]}
          touchUi={false}
          inputProps={{
            id: PERIOD_INPUT_ID,
            "aria-labelledby": PERIOD_LABEL_ID
          }}
        />
      </Box>
    </ManagementToolbarField>
  );
};
